import { Injectable, inject } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { convertToInstant } from '@app/shared/utils/date-formatter.util';
import { TranslateService } from '@ngx-translate/core';

// Import POS service API
import { PosControllerService } from '@app/api/pos-service/api/posController.service';
import { PageInfoPosResDTO } from '@app/api/pos-service/model/pageInfoPosResDTO';
import { ShiftControllerService } from '@app/api/pos-service/api/shiftController.service';
import { ShiftsResDTO } from '@app/api/pos-service/model/shiftsResDTO';
import { PageInfoShiftsResDTO } from '@app/api/pos-service/model/pageInfoShiftsResDTO';

// Import channel-service API for shop data
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { PageInfoPhysicalShopVO } from '@app/api/channel-service/model/pageInfoPhysicalShopVO';
import { ShopOption } from '../../pos-shift-open/services/pos-shift-open.service';
import { OrderPageVo, ShiftDetailResDTO } from '@app/api/pos-service';

// Local interfaces for POS Shift Closing Tracking
export interface PosShiftClosing {
  id: number;
  shift_id: string;
  posId: number;
  posCode: string;
  shopId: number;
  shopName: string;
  shopCode: string;
  shopAddress: string;
  cashierId: number;
  cashierName: string;
  openingTime: string;
  closingTime?: string;
  openingAmount: number;
  closingAmount?: number;
  totalOrderAmount?: number;
  // totalQuantity: number;
  status: 'OPEN' | 'CLOSED' | 'PENDING';
  notes?: string;
  createdBy: string;
  creationDate: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
  // Additional sections from the image
  orders?: OrderPageVo[];
  linkedInvoices?: PosInvoice[];
  salesSummary?: SalesSummary;
  paymentMethods?: PaymentMethod[];
  paymentReconciliation?: PaymentReconciliation[];
}

export interface PosInvoice {
  id: number;
  posInvoice: string;
  invoiceDate: string;
  invoiceAmount: number;
  paymentMethod: string;
  isSelected?: boolean;
}

export interface SalesSummary {
  totalAmount: number;
  totalNetAmount: number;
  totalQuantity: number;
}

export interface PaymentMethod {
  paymentMethod: string;
  paymentAmount: number;
}

export interface PaymentReconciliation {
  id: number;
  paymentMethod: string;
  openingAmount: number;
  estimatedAmount: number;
  closingAmount: number;
  difference: number;
  isSelected?: boolean;
}

export interface PosShiftClosingListResponse {
  data: PosShiftClosing[];
  totalRecords: number;
  page: number;
  size: number;
}

export interface PosShiftClosingSearchParams {
  posId?: number;
  shopId?: number;
  cashierId?: number;
  name?: string;
  startTimeFrom?: string;
  startTimeTo?: string;
  status?: number;
  page: number;
  size: number;
  sortBy?: string;
  ascending?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PosShiftCloseService {
  private translateService = inject(TranslateService);

  constructor(
    private posControllerService: PosControllerService,
    private shiftControllerService: ShiftControllerService,
    private physicalShopControllerService: PhysicalShopControllerService
  ) {}

  /**
   * Process API response that might be a Blob or JSON
   */
  private processApiResponse<T>(response: any): Observable<T> {
    if (response instanceof Blob) {
      return from(
        new Promise<T>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const textData = reader.result as string;

              // Try to parse as JSON first
              try {
                const jsonData = JSON.parse(textData);
                // Check if the response contains an error
                if (this.isErrorResponse(jsonData)) {
                  reject(
                    new Error(jsonData.message || 'Unknown error occurred')
                  );
                } else {
                  resolve(jsonData as T);
                }
              } catch (jsonError) {
                // If JSON parsing fails, check if it's a plain text response
                if (textData.trim().length > 0) {
                  // For plain text responses, return a success object
                  resolve({
                    message: textData.trim(),
                    success: true,
                    isPlainText: true,
                  } as T);
                } else {
                  // Empty response
                  resolve({
                    message: 'Empty response',
                    success: true,
                    isPlainText: true,
                  } as T);
                }
              }
            } catch (error) {
              console.error('Error processing blob data:', error);
              reject(error);
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsText(response);
        })
      );
    }

    // Check if the response contains an error
    if (this.isErrorResponse(response)) {
      throw new Error(response.message || 'Unknown error occurred');
    }

    return of(response as T);
  }

  /**
   * Check if the response contains an error
   */
  private isErrorResponse(response: any): boolean {
    // Check for common error response patterns
    if (response && typeof response === 'object') {
      // Pattern 1: {code: 500, message: 'Error message'}
      if (response.code && (response.code >= 400 || response.code === 500)) {
        return true;
      }
      // Pattern 2: {error: 'Error message'}
      if (response.error) {
        return true;
      }
      // Pattern 3: {status: 'error', message: 'Error message'}
      if (response.status === 'error') {
        return true;
      }
    }
    return false;
  }

  /**
   * Get physical shops for search dropdown (all shops)
   */
  getPhysicalShopsForSearch(): Observable<ShopOption[]> {
    const searchDTO: PhysicalShopShowDTO = {
      status: undefined,
      deleteFlag: false,
      pageRequest: {
        page: 0,
        size: 20,
      },
    };

    return this.physicalShopControllerService
      .showPage(searchDTO, 'body', false)
      .pipe(
        switchMap((response) =>
          this.processApiResponse<PageInfoPhysicalShopVO>(response)
        ),
        map((response: PageInfoPhysicalShopVO) => {
          const shops = response.pageContent || [];
          return shops.map((shop) => ({
            label: shop.name || '',
            value: shop.id || 0,
          }));
        }),
        catchError((error) => {
          console.error('Error fetching physical shops for search:', error);
          throw error;
        })
      );
  }

  /**
   * Map ShiftsResDTO to PosShiftClosing
   */
  private mapShiftToPosShiftClosing(shift: ShiftDetailResDTO): PosShiftClosing {
    // Extract POS data from the shift.pos object
    const posData = shift.pos;
    const orders = shift.orders;

    return {
      id: shift.id || 0,
      shift_id: `POS-CLOSED-${shift.id}`,
      posId: shift.posId || 0,
      posCode: posData?.code || shift.posId?.toString() || '',
      shopId: posData?.channelShopId || 0,
      shopName: posData?.channelShopName || '',
      shopCode: posData?.channelShopCode || '',
      shopAddress: posData?.channelShopAddress || '',
      cashierId: 0, // Will need to be mapped from userId
      cashierName: shift.name || '',
      openingTime: shift.startTime || '',
      closingTime: shift.endTime || '',
      openingAmount: shift.startTotalCash || 0,
      closingAmount: shift.endTotalCash || 0,
      totalOrderAmount: shift.totalOrderAmount || 0,
      // totalQuantity: shift.totalGoodsQuantity || 0,
      linkedInvoices:
        orders?.map((order) => ({
          id: order.id || 0,
          posInvoice: order.outerOrderNo || '',
          invoiceDate: order.orderTime || order.payTime || '',
          paymentMethod: this.getPaymentMethodLabel(
            this.mapPaymentType(order.payType || '')
          ),
          invoiceAmount: order.buyerRealPrice || 0,
        })) || [],
      salesSummary: {
        totalAmount: shift.totalOrderAmount || 0,
        totalNetAmount:
          orders?.reduce(
            (acc, order) => acc + (order.buyerRealPrice || 0),
            0
          ) || 0,
        totalQuantity: shift.totalGoodsQuantity || 0,
      },
      paymentMethods: this.calculatePaymentMethods(orders || []),
      paymentReconciliation: [
        {
          id: 1,
          paymentMethod: this.translateService.instant(
            'common.payment_methods.cash'
          ),
          openingAmount: shift.startTotalCash || 0,
          estimatedAmount: this.getCashPaymentAmount(orders || []),
          closingAmount: shift.endTotalCash || 0,
          difference:
            (shift.endTotalCash || 0) -
            (shift.startTotalCash || 0) -
            this.getCashPaymentAmount(orders || []),
        },
      ],
      status: this.mapShiftStatus(shift.status),
      createdBy: shift.createdBy || '',
      creationDate: shift.creationDate || '',
      lastUpdatedBy: shift.lastUpdatedBy,
      lastUpdateDate: shift.lastUpdateDate,
    };
  }

  /**
   * Map API status to our status enum
   */
  private mapShiftStatus(apiStatus?: number): 'CLOSED' {
    return 'CLOSED';
  }

  /**
   * Get POS shift closings with search parameters
   * Only returns shifts with status 2 (closed)
   */
  getPosShiftClosings(
    params: PosShiftClosingSearchParams
  ): Observable<PosShiftClosingListResponse> {
    const page = params.page || 0; // Use 0-based pagination
    const size = params.size || 20;
    const startTimeFrom = params.startTimeFrom
      ? convertToInstant(params.startTimeFrom)
      : undefined;
    const startTimeTo = params.startTimeTo
      ? convertToInstant(params.startTimeTo)
      : undefined;

    // Filter for status 2 (closed) only
    const statusFilter = [2];

    const sortBy = params.sortBy || 'endTime';
    const ascending = params.ascending !== undefined ? params.ascending : false;

    return this.shiftControllerService
      .getAllShifts(
        undefined, // userId
        params.name, // name (cashier name)
        undefined, // email
        undefined, // startTotalCash
        startTimeFrom, // startTimeFrom
        startTimeTo, // startTimeTo
        params.posId, // posId
        statusFilter, // status array - only get status 2 (closed)
        page,
        size,
        sortBy, // sortBy - default to startTime, but allow dynamic sorting
        ascending // ascending - default to false (descending)
      )
      .pipe(
        switchMap((response) =>
          this.processApiResponse<PageInfoShiftsResDTO>(response)
        ),
        map((response: PageInfoShiftsResDTO) => {
          const shifts = response.pageContent || [];
          const shiftClosings: PosShiftClosing[] = shifts.map((shift) =>
            this.mapShiftToPosShiftClosing(shift)
          );

          return {
            data: shiftClosings,
            totalRecords: response.totalElements || 0,
            page: params.page,
            size: params.size,
          };
        }),
        catchError((error) => {
          console.error('Error fetching POS shift closings:', error);
          throw error;
        })
      );
  }

  /**
   * Get POS shift closing by ID
   */
  getPosShiftClosingById(id: number): Observable<PosShiftClosing> {
    return this.shiftControllerService.getShiftById(id).pipe(
      switchMap((response) => this.processApiResponse<ShiftsResDTO>(response)),
      map((response: ShiftsResDTO) => {
        return this.mapShiftToPosShiftClosing(response);
      }),
      catchError((error) => {
        console.error('Error fetching shift closing by ID:', error);
        throw error;
      })
    );
  }

  /**
   * Get POS terminals for search dropdown
   */
  getPosTerminalsOptionsPage(params: {
    page: number;
    size: number;
    searchText: string;
  }): Observable<{
    options: Array<{ label: string; value: number }>;
    pageContentLength: number;
    totalElements?: number;
  }> {
    const searchText = params.searchText?.trim();
    return this.posControllerService
      .getAllPosPage({
        page: params.page - 1,
        size: params.size,
        sortBy: 'code',
        ascending: true,
        code: searchText || undefined,
      })
      .pipe(
        switchMap((response) =>
          this.processApiResponse<PageInfoPosResDTO>(response),
        ),
        map((response: PageInfoPosResDTO) => {
          const posTerminals = response.pageContent || [];
          const options = posTerminals.map((pos) => ({
            label: `${pos.code} (${pos.channelShopName || 'Unknown Shop'})`,
            value: pos.id || 0,
          }));
          return {
            options,
            pageContentLength: options.length,
            totalElements: response.totalElements,
          };
        }),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }

  getPosTerminalsForSearch(): Observable<
    Array<{ label: string; value: number }>
  > {
    const pageRequest = {
      page: 0,
      size: 20,
      sortBy: 'code',
      ascending: true,
    };

    return this.posControllerService.getAllPosPage(pageRequest).pipe(
      switchMap((response) =>
        this.processApiResponse<PageInfoPosResDTO>(response)
      ),
      map((response: PageInfoPosResDTO) => {
        const posTerminals = response.pageContent || [];
        return posTerminals.map((pos) => ({
          label: `${pos.code} (${pos.channelShopName || 'Unknown Shop'})`,
          value: pos.id || 0,
        }));
      }),
      catchError((error) => {
        console.error('Error fetching POS terminal options:', error);
        // Return empty array on error to prevent breaking the UI
        return of([]);
      })
    );
  }

  /**
   * Map payment type from API to standardized types
   */
  private mapPaymentType(payType: string): 'WALLET' | 'CARD' | 'CASH' {
    const normalizedType = payType?.toUpperCase() || '';

    // Map common payment types to standardized values
    if (
      normalizedType.includes('CASH') ||
      normalizedType.includes('TIỀN_MẶT')
    ) {
      return 'CASH';
    } else if (
      normalizedType.includes('CARD') ||
      normalizedType.includes('THẺ')
    ) {
      return 'CARD';
    } else if (
      normalizedType.includes('WALLET') ||
      normalizedType.includes('VÍ')
    ) {
      return 'WALLET';
    }

    // Default to CASH if type is not recognized
    return 'CASH';
  }

  /**
   * Calculate payment methods with actual amounts based on payment types
   * Only returns payment methods with amount > 0
   */
  private calculatePaymentMethods(
    orders: any[]
  ): Array<{ paymentMethod: string; paymentAmount: number }> {
    const paymentTotals: { [key: string]: number } = {
      CASH: 0,
      CARD: 0,
      WALLET: 0,
    };

    // Calculate totals for each payment type
    orders.forEach((order) => {
      const paymentType = this.mapPaymentType(order.payType || '');
      const amount = order.buyerRealPrice || 0;
      paymentTotals[paymentType] += amount;
    });

    // Return only payment methods with amount > 0
    const paymentMethods = [];

    if (paymentTotals['CASH'] > 0) {
      paymentMethods.push({
        paymentMethod: this.translateService.instant(
          'common.payment_methods.cash'
        ),
        paymentAmount: paymentTotals['CASH'],
      });
    }

    if (paymentTotals['CARD'] > 0) {
      paymentMethods.push({
        paymentMethod: this.translateService.instant(
          'common.payment_methods.card'
        ),
        paymentAmount: paymentTotals['CARD'],
      });
    }

    if (paymentTotals['WALLET'] > 0) {
      paymentMethods.push({
        paymentMethod: this.translateService.instant(
          'common.payment_methods.wallet'
        ),
        paymentAmount: paymentTotals['WALLET'],
      });
    }

    return paymentMethods;
  }

  /**
   * Get cash payment amount from orders
   */
  private getCashPaymentAmount(orders: any[]): number {
    return orders
      .filter((order) => this.mapPaymentType(order.payType || '') === 'CASH')
      .reduce((acc, order) => acc + (order.buyerRealPrice || 0), 0);
  }

  /**
   * Get translated payment method label
   */
  private getPaymentMethodLabel(
    paymentType: 'WALLET' | 'CARD' | 'CASH'
  ): string {
    switch (paymentType) {
      case 'CASH':
        return this.translateService.instant('common.payment_methods.cash');
      case 'CARD':
        return this.translateService.instant('common.payment_methods.card');
      case 'WALLET':
        return this.translateService.instant('common.payment_methods.wallet');
      default:
        return this.translateService.instant('common.payment_methods.cash');
    }
  }
}
